import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { inr, inrCompact, num } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Lock, Unlock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function MonthClose() {
  const { user, can } = useAuth();
  const [months, setMonths] = useState([]);
  const [selected, setSelected] = useState(null);
  const [stmt, setStmt] = useState(null);
  const nav = useNavigate();

  const loadList = () => api.get("/month-close").then(({ data }) => { setMonths(data); if (!selected && data[0]) setSelected(data[0].month); });
  useEffect(() => { loadList(); }, []);
  useEffect(() => { if (selected) api.get(`/month-close/${selected}`).then(({ data }) => setStmt(data)); }, [selected]);

  const canLock = can("mod.month_close");
  const canUnlock = can("mod.month_unlock");

  const lock = async () => {
    try { await api.post(`/month-close/${selected}/lock`); toast.success(`${selected} locked`); loadList(); api.get(`/month-close/${selected}`).then(({ data }) => setStmt(data)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const unlock = async () => {
    try { await api.post(`/month-close/${selected}/unlock`); toast.success(`${selected} unlocked`); loadList(); api.get(`/month-close/${selected}`).then(({ data }) => setStmt(data)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <PageHeader title="Month-End Closing" subtitle="Generate the monthly statement, then lock it so payouts freeze. Only a Super Admin can reopen a locked month." />

      <div className="mb-5 flex flex-wrap gap-2">
        {months.map((m) => (
          <button key={m.month} onClick={() => setSelected(m.month)} data-testid={`month-tab-${m.month}`}
            className={`flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition ${selected === m.month ? "border-primary bg-primary/10 text-primary" : "border-border bg-card text-muted-foreground hover:bg-accent"}`}>
            {m.locked ? <Lock className="h-3.5 w-3.5 text-amber-500" /> : <Unlock className="h-3.5 w-3.5" />}
            {m.month}
          </button>
        ))}
      </div>

      {stmt && (
        <>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${stmt.locked ? "bg-amber-500/15 text-amber-500" : "bg-emerald-500/15 text-emerald-500"}`}>
                {stmt.locked ? <Lock className="h-5 w-5" /> : <ShieldCheck className="h-5 w-5" />}
              </div>
              <div>
                <p className="font-head text-lg font-bold">{stmt.month} {stmt.locked ? "· Locked" : "· Open"}</p>
                <p className="text-xs text-muted-foreground">{stmt.locked && stmt.lock ? `Locked by ${stmt.lock.locked_by}` : `${num(stmt.dp_count)} partners · ${num(stmt.total_orders)} orders`}</p>
              </div>
            </div>
            <div className="flex gap-2">
              {!stmt.locked && canLock && <Button onClick={lock} data-testid="lock-month-btn"><Lock className="mr-1.5 h-4 w-4" /> Lock Month</Button>}
              {stmt.locked && canUnlock && <Button variant="outline" onClick={unlock} data-testid="unlock-month-btn"><Unlock className="mr-1.5 h-4 w-4" /> Unlock (Super Admin)</Button>}
              {stmt.locked && !canUnlock && <span className="rounded-lg bg-secondary px-3 py-2 text-xs text-muted-foreground">Locked — Super Admin only</span>}
            </div>
          </div>

          <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="rounded-xl border border-border bg-card p-5">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-500">Gross Earnings</p>
              <div className="mt-3 space-y-1.5 text-sm">
                <Row k="Base Order Earnings" v={stmt.base} /><Row k="Rain Charges" v={stmt.rain} /><Row k="Extra Distance" v={stmt.extra_distance} />
                <Row k="Other Incentives" v={stmt.other_incentives} /><Row k="Campaign Incentives" v={stmt.campaign_incentives} />
              </div>
              <div className="mt-3 flex justify-between border-t border-border pt-2 font-bold"><span>Gross</span><span className="tabular">{inr(stmt.gross)}</span></div>
            </div>
            <div className="rounded-xl border border-border bg-card p-5">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-rose-500">Deductions</p>
              <div className="mt-3 space-y-1.5 text-sm"><Row k="EV Rent Collected" v={stmt.ev_rent} /><Row k="Other Adjustments" v={stmt.adjustments} /></div>
              <div className="mt-3 flex justify-between border-t border-border pt-2 font-bold"><span>Total</span><span className="tabular">{inr(stmt.ev_rent + stmt.adjustments)}</span></div>
            </div>
            <div className="flex flex-col justify-center rounded-xl border border-primary/30 bg-primary/5 p-5 text-center">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Net Payable</p>
              <p className="mt-1 font-head text-3xl font-bold tabular text-primary" data-testid="month-net-payable">{inrCompact(stmt.net_payable)}</p>
              <p className="mt-1 text-xs text-muted-foreground">{num(stmt.total_orders)} orders across {num(stmt.dp_count)} DPs</p>
            </div>
          </div>

          <div className="overflow-hidden rounded-xl border border-border bg-card">
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent"><TableHead>DP</TableHead><TableHead className="text-right">Orders</TableHead><TableHead className="text-right">Gross</TableHead><TableHead className="text-right">EV Rent</TableHead><TableHead className="text-right">Net Payable</TableHead></TableRow></TableHeader>
              <TableBody>
                {stmt.partners.map((p) => (
                  <TableRow key={p.dp_id} className="cursor-pointer" onClick={() => nav(`/dp/${p.dp_id}`)} data-testid={`month-dp-${p.fe_id}`}>
                    <TableCell className="font-semibold">{p.name}<span className="ml-1 font-mono text-xs text-muted-foreground">{p.fe_id}</span></TableCell>
                    <TableCell className="text-right tabular">{num(p.orders)}</TableCell>
                    <TableCell className="text-right tabular">{inr(p.gross)}</TableCell>
                    <TableCell className="text-right tabular text-rose-500">-{inr(p.ev_rent)}</TableCell>
                    <TableCell className="text-right tabular font-bold text-emerald-500">{inr(p.net_payable)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}
const Row = ({ k, v }) => (<div className="flex justify-between"><span className="text-muted-foreground">{k}</span><span className="tabular">{inr(v)}</span></div>);
